/**
 * Status module (#92 follow-up): a list of health checks — "is IMSight's
 * backend working right now?" — built only from the existing Gateway health
 * data in useGatewayStore. No new Gateway calls, no TYPO3 changes, no
 * gateway/auth code changes, and no connection-mode wiring (that is never
 * shown on this page).
 */

import template from './status.template.html?raw';
import {
  useGatewayStore,
  type GatewayState,
} from '../../../stores/gateway-store';
import type { AppModule } from '../../shell/module.types';
import type { TranslationParams } from '../../../i18n/ui-manager.types';

type HealthKey = GatewayState['health'] | 'unknown';
type ConnectionKey = GatewayState['connection'] | 'unknown';
type CheckState = 'pass' | 'warn' | 'fail' | 'unknown';
type CheckId = 'connected' | 'health' | 'responseTime' | 'failures';

/** Response-time thresholds (ms): pass under WARN, warn under FAIL, fail at/above FAIL. */
export const RESPONSE_TIME_WARN_MS = 1000;
export const RESPONSE_TIME_FAIL_MS = 5000;

/** Consecutive-failures thresholds: pass at 0, warn under FAIL, fail at/above FAIL. */
export const FAILURES_WARN_COUNT = 1;
export const FAILURES_FAIL_COUNT = 3;

/** Decorative glyph per state; the accessible label comes from the state text span. */
const INDICATOR_GLYPH: Record<CheckState, string> = {
  pass: '✓', // ✓
  warn: '!',
  fail: '✕', // ✕
  unknown: '–', // –
};

export interface StatusModuleDeps {
  t: (key: string, params?: TranslationParams) => string;
  /** Re-render only while visible, mirroring the dashboard module. */
  onLanguageChange: (callback: () => void) => void;
  /** Injectable clock for tests; defaults to Date.now. */
  now?: () => number;
}

interface CheckResult {
  id: CheckId;
  state: CheckState;
  value: string;
}

/** 'unknown' while not yet initialized or before the first poll. */
function currentHealthKey(state: GatewayState): HealthKey {
  if (!state.initialized || !state.lastPollStatus) return 'unknown';
  return state.health;
}

/**
 * 'unknown' while not yet initialized or before the first poll — same rule
 * as currentHealthKey. The store defaults `connection` to 'disconnected',
 * so without this the Connected check would read a false "fail:
 * Disconnected" before any poll has actually run.
 */
function currentConnectionKey(state: GatewayState): ConnectionKey {
  if (!state.initialized || !state.lastPollStatus) return 'unknown';
  return state.connection;
}

function healthCheckState(healthKey: HealthKey): CheckState {
  switch (healthKey) {
    case 'healthy':
      return 'pass';
    case 'degraded':
      return 'warn';
    case 'unhealthy':
      return 'fail';
    default:
      return 'unknown';
  }
}

function connectedCheckState(connectionKey: ConnectionKey): CheckState {
  switch (connectionKey) {
    case 'connected':
      return 'pass';
    case 'disconnected':
      return 'fail';
    default:
      return 'unknown';
  }
}

function responseTimeState(ms: number | undefined): CheckState {
  if (ms === undefined) return 'unknown';
  if (ms < RESPONSE_TIME_WARN_MS) return 'pass';
  if (ms < RESPONSE_TIME_FAIL_MS) return 'warn';
  return 'fail';
}

function failuresState(count: number): CheckState {
  if (count < FAILURES_WARN_COUNT) return 'pass';
  if (count < FAILURES_FAIL_COUNT) return 'warn';
  return 'fail';
}

export function createStatusModule(deps: StatusModuleDeps): AppModule {
  const now = deps.now ?? (() => Date.now());
  let root: HTMLElement | null = null;
  let visible = false;
  let unsubscribe: (() => void) | undefined;

  const formatRelativeTime = (timestamp: number | null): string => {
    if (timestamp === null) return deps.t('statusPage.never');
    const seconds = Math.max(0, Math.round((now() - timestamp) / 1000));
    if (seconds < 1) return deps.t('statusPage.justNow');
    if (seconds < 60) return deps.t('statusPage.secondsAgo', { seconds });
    const minutes = Math.round(seconds / 60);
    return deps.t('statusPage.minutesAgo', { minutes });
  };

  const buildChecks = (state: GatewayState): CheckResult[] => {
    const healthKey = currentHealthKey(state);
    const connectionKey = currentConnectionKey(state);
    const responseTime = state.lastPollStatus?.responseTime;
    const failures = state.consecutiveFailures;

    return [
      {
        id: 'connected',
        state: connectedCheckState(connectionKey),
        value: deps.t(`statusPage.checks.connected.${connectionKey}`),
      },
      {
        id: 'health',
        state: healthCheckState(healthKey),
        value: deps.t(`statusPage.checks.health.${healthKey}`),
      },
      {
        id: 'responseTime',
        state: responseTimeState(responseTime),
        value:
          responseTime === undefined
            ? deps.t('statusPage.checks.responseTime.unknown')
            : `${responseTime}${deps.t('units.ms')}`,
      },
      {
        id: 'failures',
        state: failuresState(failures),
        value: String(failures),
      },
    ];
  };

  const render = (): void => {
    if (!root) return;
    const state = useGatewayStore.getState();
    const checks = buildChecks(state);

    for (const check of checks) {
      const item = root.querySelector<HTMLElement>(
        `[data-status-check="${check.id}"]`
      );
      if (!item) continue;

      const indicator = item.querySelector<HTMLElement>(
        '[data-status-indicator]'
      );
      if (indicator) {
        indicator.className = `status__indicator status__indicator--${check.state}`;
        indicator.textContent = INDICATOR_GLYPH[check.state];
      }

      const stateText = item.querySelector<HTMLElement>(
        '[data-status-check-state]'
      );
      if (stateText) {
        stateText.textContent = deps.t(
          `statusPage.checks.state.${check.state}`
        );
      }

      const value = item.querySelector<HTMLElement>(
        '[data-status-check-value]'
      );
      if (value) {
        value.textContent = check.value;
      }
    }

    const lastCheck = root.querySelector<HTMLElement>(
      '[data-status-last-check]'
    );
    if (lastCheck) {
      lastCheck.textContent = formatRelativeTime(state.lastPollTime);
    }

    const lastError = root.querySelector<HTMLElement>(
      '[data-status-last-error]'
    );
    const lastErrorText = root.querySelector<HTMLElement>(
      '[data-status-last-error-text]'
    );
    const error = state.lastPollStatus?.error;
    const isFailing = checks.some((check) => check.state === 'fail');
    if (lastError) {
      lastError.hidden = !(isFailing && error);
    }
    if (lastErrorText) {
      // textContent, never innerHTML: lastPollStatus.error is untrusted.
      lastErrorText.textContent = isFailing && error ? error : '';
    }
  };

  const subscribe = (): void => {
    if (unsubscribe) return;
    unsubscribe = useGatewayStore.subscribe(() => {
      if (visible) render();
    });
  };

  return {
    id: 'status',
    route: '/status',
    labelKey: 'modules.status.label',
    descriptionKey: 'modules.status.description',
    icon: 'status',
    enabled: true,
    showOnDashboard: true,

    mount(outlet) {
      outlet.innerHTML = template;
      root = outlet;
      deps.onLanguageChange(() => {
        if (visible) render();
      });
    },

    onEnter() {
      visible = true;
      // Re-sync first: changes made while hidden were missed (no subscription).
      render();
      subscribe();
    },

    onLeave() {
      visible = false;
      unsubscribe?.();
      unsubscribe = undefined;
    },

    getStatus() {
      const state = useGatewayStore.getState();
      const healthKey = currentHealthKey(state);
      return deps.t('modules.status.status', {
        state: deps.t(`statusBar.health.${healthKey}`),
      });
    },
  };
}
